import { motion } from "framer-motion";
import { ArrowRight, Mail } from "lucide-react";
import { Reveal, MaskedLines } from "@/components/site/Reveal";

const FOUNDER_IMG = "/assets/founder.jpeg";
const AUDITOR_IMG = "/assets/Formal Business Portrait.png";
const JANVI_IMG = "/assets/janvi-pagar.png";

export default function AboutUs() {
  return (
    <main data-testid="about-us-page" className="bg-[#f7f4ee] overflow-x-clip">
      {/* HERO */}
      <section className="pt-40 md:pt-48 pb-8 md:pb-12 overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.6 }}
            className="overline-label mb-6 text-left"
            data-testid="about-us-overline"
          >
            About Us
          </motion.p>
          <h1 className="font-display text-[#12294d] text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.1] max-w-4xl" data-testid="about-us-heading">
            <MaskedLines
              lines={[
                <>The Story Behind Med<span className="text-[#2ab5cf]">Verify</span></>,
              ]}
              startDelay={0.15}
            />
          </h1>
        </div>
      </section>

      {/* STORY + EXECUTIVE BACKGROUND */}
      <section className="py-16 md:py-24 overflow-hidden">
        <div className="max-w-6xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-12 lg:gap-16 items-start">
          <Reveal>
            <div className="space-y-6 text-slate-600 leading-relaxed text-base md:text-lg" data-testid="about-us-story">
              <p>
                After over a decade across the US and Canada leading healthtech and care delivery
                initiatives, I returned to India to launch MedVerify Services—a Mumbai-based healthcare
                company dedicated to safeguarding corporate workforces.
              </p>
              <p>
                Employees in the modern workplaces face an unprecedented surge in chronic stress, mental
                fatigue, cardiovascular conditions, and lifestyle diseases like diabetes. For organizations,
                the business impact is severe: rising absenteeism, lower productivity, escalating insurance
                premiums, and high attrition.
              </p>
              <p>
                At MedVerify Services we partner with you to determine your unique pain points and needs.
                We collaborate with you to offer a range of curated health and wellness services that will
                make your workforce productive, healthier, resilient and happier!
              </p>
              <p>
                My career has took me around the world and also awarded me the privilege of studying and
                completing three Master's degrees (Gerontology, MBA and MS in IT) in the US.
              </p>
              <p>
                I have spearheaded healthcare and technology initiatives with premier North American
                institutions, including Boston Children's Hospital, eClinicalworks, British Columbia's
                Provincial Health Services Authority (Vancouver) as well as in FinTech with Gartner
                ranked #1 Global Relay in the archiving communications domain.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <motion.div
              whileHover={{ scale: 1.02, rotate: 0.8 }}
              transition={{ type: "spring", stiffness: 200, damping: 18 }}
              className="lg:sticky lg:top-32"
            >
              <img
                src={FOUNDER_IMG}
                alt="Founder of MedVerify Services"
                data-testid="about-us-founder-photo"
                className="rounded-3xl w-full max-w-sm mx-auto object-cover shadow-xl shadow-[#1a3a6b]/10 border border-white"
              />
              <div className="mt-5 text-center max-w-sm mx-auto" data-testid="about-us-founder-name">
                <p className="font-display text-[#12294d] text-xl tracking-tight">Vasant Nilkund</p>
                <p className="mt-1 text-sm text-slate-500 font-medium tracking-wide">Chief Executive Officer</p>
              </div>
            </motion.div>
          </Reveal>
        </div>
      </section>

      {/* CHIEF ISO AUDITOR */}
      <section className="pb-16 md:pb-24 overflow-hidden" data-testid="about-us-chief-auditor">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <Reveal>
            <div className="py-7 md:py-10 lg:py-12">
              <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-10 lg:gap-14 items-start">
                <motion.div
                  whileHover={{ scale: 1.02, rotate: -0.5 }}
                  transition={{ type: "spring", stiffness: 200, damping: 18 }}
                >
                  <img
                    src={AUDITOR_IMG}
                    alt="Shrinivas Nilkund, Chief ISO Auditor"
                    className="rounded-3xl w-full max-w-[300px] aspect-square mx-auto object-cover object-top shadow-lg border border-slate-100 bg-slate-50"
                  />
                  <div className="mt-5 text-center">
                    <p className="font-display text-[#12294d] text-xl tracking-tight">Shrinivas Nilkund</p>
                    <p className="mt-1 text-sm text-slate-500 font-medium tracking-wide">Chief ISO Auditor</p>
                  </div>
                </motion.div>

                <div className="text-slate-600 leading-relaxed">
                  <p className="text-base md:text-lg">
                    <strong className="text-[#12294d]">Shrinivas Nilkund</strong> is the Chief Auditor at MedVerify Services. A marine engineer and lead auditor, he brings over 30 years of experience in the marine industry. He has worked with multiple multinational corporations and led their training and development teams.
                  </p>
                  <p className="mt-5 text-base md:text-lg">
                    As Chief Auditor, Shrinivas Nilkund conducts audits of clinics, ship management companies, and vessels.
                  </p>

                  <div className="mt-8">
                    <h2 className="font-display text-[#12294d] text-2xl tracking-tight">His ISO Credentials &amp; Training Scope are as follows:</h2>
                    <ul className="mt-4 space-y-3 list-disc pl-5">
                      <li>Lead Auditor &amp; Trainer for: ISO-9001 (QMS), 14001 (EMS), 45001 (OHSMS), 21001 (EOMS), and 27001 (ISMS).</li>
                      <li>Empanelled Auditor and Trainer with ISSPL (formerly IRQS).</li>
                    </ul>
                  </div>

                  <div className="mt-8">
                    <h2 className="font-display text-[#12294d] text-2xl tracking-tight">Key Achievements &amp; Audits conducted:</h2>
                    <ul className="mt-4 space-y-3 list-disc pl-5">
                      <li>
                        OVER 1000 ISO Audits conducted, including for major clients such as:
                        <ul className="mt-3 space-y-2 list-disc pl-6">
                          <li>Great Eastern Shipping (Greatship India)</li>
                          <li>Seven Islands</li>
                          <li>SCI, India Maritime Institute</li>
                          <li>VR MARINE</li>
                          <li>Prayati Shipping</li>
                          <li>SETRAC College</li>
                          <li>JSW Ports- Jaigadh, Ratangiri</li>
                          <li>South West Port Goa</li>
                          <li>JSW Port Mangalore</li>
                          <li>SCI</li>
                          <li>Adani Ports</li>
                          <li>JM Baxi</li>
                          <li>KICT Kandla</li>
                          <li>Paradip Int. Container Terminal</li>
                          <li>Chowgule Shipbuilding, Goa</li>
                          <li>Ratnagiri</li>
                        </ul>
                      </li>
                      <li>Over 80 IRCA (International Register of Certificated Auditors) approved ISO LEAD AUDITOR TRAININGS (each of 5 days duration).</li>
                      <li>Trained over 1000 attendees to date.</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* TECHNICAL OFFICER */}
      <section className="pb-16 md:pb-24 overflow-hidden" data-testid="about-us-technical-officer">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <Reveal>
            <div className="py-7 md:py-10 lg:py-12">
              <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-10 lg:gap-14 items-start">
                <motion.div
                  whileHover={{ scale: 1.02, rotate: -0.5 }}
                  transition={{ type: "spring", stiffness: 200, damping: 18 }}
                >
                  <img
                    src="/assets/janvi-pagar.png"
                    alt="Janvi Pagar, Technical Officer"
                    className="rounded-3xl w-full max-w-[300px] h-auto mx-auto object-contain"
                  />
                </motion.div>
                <div>
                  <h2 className="font-display text-[#12294d] text-3xl md:text-4xl tracking-tight">Janvi Pagar</h2>
                  <p className="mt-2 text-[#2ab5cf] font-semibold tracking-wide">Technical Officer</p>
                  <div className="mt-7 space-y-5 text-slate-600 leading-relaxed text-base md:text-lg">
                    <p>
                      As Technical Officer at MedVerify Services, my role encompasses the planning, development,
                      implementation, and continuous improvement of the company’s digital healthcare systems and
                      technology initiatives.
                    </p>
                    <p>
                      A key part of this responsibility is translating business and operational requirements into practical,
                      efficient, and scalable digital solutions. The work spans the technology lifecycle—from defining
                      workflows and system requirements to coordinating development, testing functionality, addressing
                      operational challenges, and supporting deployment—with an emphasis on creating systems that are
                      intuitive, reliable, and aligned with organizational and user needs.
                    </p>
                    <p>
                      The broader objective is to strengthen MedVerify Services’ digital capabilities through technology
                      that streamlines operations, enhances user experience, and enables more efficient healthcare delivery.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-24 overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <div className="relative rounded-[2rem] p-10 md:p-14 bg-white border border-[#1a3a6b]/10 shadow-xl shadow-[#1a3a6b]/5 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8">
              <p className="font-display text-[#12294d] text-2xl md:text-3xl tracking-tight max-w-2xl" data-testid="about-us-cta-text">
                Let's connect to explore how MedVerify Services can transform your corporate wellness strategy.
              </p>
              <a href="mailto:info@medverifys.com" data-testid="about-us-cta-button" className="btn-primary rounded-full px-8 py-4 text-sm font-bold inline-flex items-center justify-center gap-2 shrink-0">
                <Mail size={16} /> Let's Connect <ArrowRight size={16} />
              </a>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
